import {
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
  type KeyboardEvent,
  forwardRef,
} from "react";
import styled, { css, keyframes } from "styled-components";
import { createPortal } from "react-dom";
import { theme } from "../../styles/theme";
import XIcon from "../../assets/icons/XIcon";
import type { ICloseButtonProps } from "../Modal/types";

// =============================================================================
// TYPES
// =============================================================================

export type ModalSize = "xs" | "sm" | "md" | "lg" | "xl" | "full";

export interface GenericModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal content */
  children: ReactNode;
  /** Optional header content */
  header?: ReactNode;
  /** Optional footer content */
  footer?: ReactNode;
  /** Modal size */
  size?: ModalSize;
  /** Whether to center the modal vertically */
  isCentered?: boolean;
  /** Whether clicking overlay should close modal */
  closeOnOverlayClick?: boolean;
  /** Whether pressing Escape should close modal */
  closeOnEsc?: boolean;
  /** Whether to show close button */
  showCloseButton?: boolean;
  /** Custom class name for styling */
  className?: string;
  /** Whether the modal content has padding */
  noPadding?: boolean;
  /** Custom overlay opacity */
  overlayOpacity?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Accessibility label */
  ariaLabel?: string;
}

// =============================================================================
// ANIMATIONS
// =============================================================================

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

interface OverlayProps {
  $isClosing?: boolean;
  $opacity?: number;
}

const Overlay = styled.div<OverlayProps>`
  position: fixed;
  inset: 0;
  z-index: 19999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, ${({ $opacity }) => $opacity ?? 0.5});
  animation: ${({ $isClosing }) => ($isClosing ? fadeOut : fadeIn)} 0.2s ease-out;
  backdrop-filter: blur(2px);
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const sizeStyles = {
  xs: css`
    max-width: 320px;
  `,
  sm: css`
    max-width: 480px;
  `,
  md: css`
    max-width: 600px;
  `,
  lg: css`
    max-width: 800px;
  `,
  xl: css`
    max-width: 1024px;
  `,
  full: css`
    max-width: calc(100vw - 2rem);
    max-height: calc(100vh - 2rem);
    width: 100%;
    height: 100%;
  `,
};

interface ContentProps {
  $size: ModalSize;
  $isCentered?: boolean;
  $isClosing?: boolean;
  $noPadding?: boolean;
}

const Content = styled.div<ContentProps>`
  position: relative;
  width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: ${({ $isClosing }) => ($isClosing ? fadeOut : slideUp)} 0.3s ease-out;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${({ $size }) => sizeStyles[$size]}

  ${({ $isCentered }) =>
    !$isCentered &&
    css`
      margin-top: 4rem;
      align-self: flex-start;
    `}

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 8px;
    max-height: calc(100vh - 1rem);
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${theme.colors.gray};
  flex-shrink: 0;

  h2 {
    margin: 0;
    font-size: ${theme.fontSize.xl};
    font-weight: 600;
    color: ${theme.colors.text};
  }

  @media (max-width: 768px) {
    padding: 1rem;

    h2 {
      font-size: ${theme.fontSize.lg};
    }
  }
`;

interface MainContentProps {
  $noPadding?: boolean;
}

const Main = styled.main<MainContentProps>`
  flex: 1;
  overflow-y: auto;
  padding: ${({ $noPadding }) => ($noPadding ? "0" : "1.5rem")};

  @media (max-width: 768px) {
    padding: ${({ $noPadding }) => ($noPadding ? "0" : "1rem")};
  }
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid ${theme.colors.gray};
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

export const CloseButton = styled.div<ICloseButtonProps>`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 35px;
  height: 35px;
  z-index: 19000;
  color: black;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0; /* Prevents button from shrinking */

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  img,
  svg {
    width: 20px;
    height: 20px;
    display: block;
    color: #555;
  }
`;


const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.gray};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: ${rotate} 0.8s linear infinite;
`;

// =============================================================================
// ICONS
// =============================================================================

// =============================================================================
// COMPONENT
// =============================================================================

export const GenericModal = forwardRef<HTMLDivElement, GenericModalProps>(
  (
    {
      isOpen,
      onClose,
      children,
      header,
      footer,
      size = "md",
      isCentered = true,
      closeOnOverlayClick = true,
      closeOnEsc = true,
      showCloseButton = true,
      className,
      noPadding = false,
      overlayOpacity,
      isLoading = false,
      ariaLabel,
    },
    ref
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Handle Escape key
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (closeOnEsc && event.key === "Escape") {
          event.preventDefault();
          onClose();
        }
      },
      [closeOnEsc, onClose]
    );

    // Handle overlay click
    const handleOverlayClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (
          closeOnOverlayClick &&
          contentRef.current &&
          !contentRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      },
      [closeOnOverlayClick, onClose]
    );

    // Lock body scroll and manage focus
    useEffect(() => {
      if (isOpen) {
        previousActiveElement.current = document.activeElement as HTMLElement;
        document.body.style.overflow = "hidden";
        contentRef.current?.focus();

        return () => {
          document.body.style.overflow = "";
          previousActiveElement.current?.focus();
        };
      }
    }, [isOpen]);

    if (!isOpen) {
      return null;
    }

    const modalContent = (
      <Overlay
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        $opacity={overlayOpacity}
        onClick={handleOverlayClick}
        onKeyDown={handleKeyDown as unknown as React.KeyboardEventHandler<HTMLDivElement>}
      >
        <Content
          ref={(node) => {
            (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          $size={size}
          $isCentered={isCentered}
          $noPadding={noPadding}
          className={className}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          {isLoading && (
            <LoadingOverlay>
              <Spinner />
            </LoadingOverlay>
          )}

          {showCloseButton && (
            <CloseButton
              onClick={onClose}
              aria-label="Fechar modal"
            >
              <XIcon />
            </CloseButton>
          )}

          {header && (
            <Header>
              {typeof header === "string" ? <h2>{header}</h2> : header}
            </Header>
          )}

          <Main $noPadding={noPadding}>{children}</Main>

          {footer && <Footer>{footer}</Footer>}
        </Content>
      </Overlay>
    );

    // Render to portal
    return createPortal(modalContent, document.body);
  }
);

GenericModal.displayName = "GenericModal";

// =============================================================================
// CONVENIENCE EXPORTS (to match Chakra UI API)
// =============================================================================

// These are for backward compatibility with Chakra UI patterns
export const Modal = GenericModal;
export const ModalOverlay = styled.div``; // Placeholder, handled internally
export const ModalContent = styled.div``; // Placeholder, handled internally
export const ModalHeader = Header;
export const ModalBody = Main;
export const ModalFooter = Footer;
export const ModalCloseButton = CloseButton;

export default GenericModal;
